import { getCookie, setCookie, deleteCookie } from '../utils/dom';
import { urlifyAllowAccents, camelCase, atob } from '../utils/string';
import { deepClone } from '../utils/object';
import {
	SUBSCRIBED_PRODUCT_ALERT,
	VOUCHER_INPUT,
	VOUCHER_ERROR,
	RECENTLY_VIEWED,
	LIST_ITEM_CACHE,
	SELECT_SIZE,
	ADDED_TO_BAG,
	ADD_TO_BAG_ERROR,
	UPDATE_ADDRESS,
	UPDATE_SELECTION_CACHE,
	UPDATE_SELECTION,
	UPDATE_ADDITIONAL_INFORMATION,
	UPDATE_ERRORS,
	BOOLEAN_CHANGE,
	DESTROY_SELECTION,
} from '../actions/checkout';

let token = null;
if (!token && typeof document !== 'undefined') token = getCookie('selection');

const initialState = {
	items: {},
	selectionCache: [],
	recentlyViewed: [],
	useShippingAddress: false,
	initiatedScript: false,
	productAlerts: [],
	vouchers: {
		currentValue: '',
		error: false,
	},
	addressRetrieved: {
		successful: false,
		error: false,
	},
	payment: {
		address: {},
		shippingAddress: {},
		paymentFailedPage: '',
		paymentReturnPage: '',
		termsAndConditions: false,
		additionalInformation: [],
	},
	shippingAvailable: {},
	paymentReturn: {},
	addToBagError: false,
	selectionError: false,
	selectionErrorStock: false,
	selection: {},
	destroySelection: false,
};

if (token && token !== '1') initialState.selection.token = token;

const checkout = (state = initialState, action = {}) => {
	switch (action.type) {
		case SUBSCRIBED_PRODUCT_ALERT: {
			return {
				...state,
				productAlerts: state.productAlerts.indexOf(action.selected) === -1 ? [...state.productAlerts, action.selected] : state.productAlerts,
			};
		}

		case SELECT_SIZE: {
			return {
				...state,
				selectionError: false,
				selectionErrorStock: false,
				addToBagError: false,
				items: {
					...state.items,
					[action.product]: {
						...state.items[action.product],
						size: action.item,
						quantity: action.quantity,
					},
				},
			};
		}

		case ADDED_TO_BAG: {
			return {
				...state,
				addedToBag: action.open,
			};
		}

		case ADD_TO_BAG_ERROR: {
			return {
				...state,
				addToBagError: true,
			};
		}

		case RECENTLY_VIEWED: {
			let recentlyViewed = typeof localStorage !== 'undefined' ? JSON.parse(localStorage.getItem('recentlyViewed')) : [];
			recentlyViewed = recentlyViewed || [];
			const productExistsIndex = recentlyViewed.indexOf(action.product);

			if (productExistsIndex === -1) {
				recentlyViewed = [action.product, ...recentlyViewed.slice(0)].slice(0, 4);
			} else {
				recentlyViewed = [action.product, ...recentlyViewed.slice(0, productExistsIndex), ...recentlyViewed.slice(productExistsIndex + 1)].slice(0, 4);
			}

			if (typeof localStorage !== 'undefined' && recentlyViewed.length > 0) localStorage.setItem('recentlyViewed', JSON.stringify(recentlyViewed));
			return {
				...state,
				recentlyViewed,
			};
		}

		case LIST_ITEM_CACHE: {
			let listItemCache = typeof localStorage !== 'undefined' ? JSON.parse(localStorage.getItem('listItemCache')) : {};
			listItemCache = listItemCache || {};

			if (action.productId && action.listName) listItemCache[action.productId] = action.listName;

			if (typeof localStorage !== 'undefined' && Object.keys(listItemCache).length > 0) localStorage.setItem('listItemCache', JSON.stringify(listItemCache));
			return {
				...state,
				listItemCache,
			};
		}

		case 'INIT_SELECTION_CACHE': {
			let updatedCache;
			if (Array.isArray(action.initCache)) {
				updatedCache = [...action.initCache];
			} else if (typeof action.initCache === 'object' && action.initCache !== null) {
				updatedCache = Object.keys(action.initCache).map((sC) => action.initCache[sC]);
			} else {
				updatedCache = [];
			}

			return {
				...state,
				selectionCache: updatedCache.filter((sC) => sC),
			};
		}

		case 'REPLACE_SELECTION_CACHE': {
			let updatedCache = [];
			if (Array.isArray(state.selectionCache)) {
				updatedCache = [...state.selectionCache];
			} else if (typeof state.selectionCache === 'object' && state.selectionCache !== null) {
				updatedCache = Object.keys(state.selectionCache).map((sC) => state.selectionCache[sC]);
			}

			state.selection.items.forEach((item, i) => {
				const selectionVariantId = item.id;
				let previousIndex = -1;
				updatedCache.forEach((cachedItem, j) => {
					if (atob(cachedItem.selectionVariantId) === atob(selectionVariantId)) previousIndex = j;
				});

				if (previousIndex === -1 && updatedCache.length !== 0) {
					// Doesn't exist in selection cache. Remove from cache
					updatedCache = [...updatedCache.slice(0, i), ...updatedCache.slice(i + 1)];
				} else {
					// Product is still relevant. Merge with cache
					let translatedId = action.productIdLookup[item.cacheId.replace(/\D+/g, '')];
					if (translatedId) translatedId = item.cacheId.replace(/\d+/, translatedId);
					const cache = deepClone(action.productCache[(translatedId || item.cacheId)]);
					cache.category = updatedCache?.[previousIndex || i]?.category;
					cache.selectionVariantId = selectionVariantId;

					updatedCache[(previousIndex !== -1 ? previousIndex : i)] = cache;
				}
			});

			localStorage.setItem('selectionCache', JSON.stringify(updatedCache));

			return {
				...state,
				selectionCache: updatedCache,
			};
		}

		case UPDATE_SELECTION_CACHE: {
			let updatedCache;
			if (Array.isArray(state.selectionCache)) {
				updatedCache = [...state.selectionCache];
			} else if (typeof state.selectionCache === 'object' && state.selectionCache !== null) {
				updatedCache = Object.keys(state.selectionCache).map((sC) => state.selectionCache[sC]);
			} else {
				updatedCache = [];
			}

			const cachedItems = !Array.isArray(action.cachedItem) ? [action.cachedItem] : action.cachedItem;
			const lineItems = !Array.isArray(action.lineItems) ? [action.lineItems] : action.lineItems;

			lineItems.forEach((lineItem, i) => {
				let previousIndex = -1;

				updatedCache.forEach((cachedItem, j) => {
					if (cachedItem && cachedItem.selectionVariantId === cachedItems[i].selectionVariantId) previousIndex = j;
				});

				if (lineItem.quantity === 0) {
					// Remove from cache
					updatedCache = [...updatedCache.slice(0, previousIndex), ...updatedCache.slice(previousIndex + 1)];
				} else if (previousIndex === -1) {
					// Add to cache
					const cache = deepClone(cachedItems[i]);
					cache.selectionVariantId = lineItem.variantId;
					cache.category = action.category;

					updatedCache = [cache, ...updatedCache];
				}
			});

			localStorage.setItem('selectionCache', JSON.stringify(updatedCache));

			return {
				...state,
				selectionCache: updatedCache,
			};
		}

		case 'INITIATE_SELECTION': {
			return {
				...state,
				paymentReturn: {
					...state.paymentReturn,
					paymentHTML: action.formHtml,
				},
			};
		}

		case UPDATE_ADDRESS: {
			const paymentInfo = action.group ? {
				...state.payment,
				[action.group]: {
					...state.payment[action.group],
					[action.id]: action.value,
				},
			} : {
				...state.payment,
				[action.id]: action.value,
			};

			if (typeof localStorage !== 'undefined') {
				const savePaymentInfo = deepClone(paymentInfo);
				delete savePaymentInfo.password;
				localStorage.setItem('paymentInfo', JSON.stringify(savePaymentInfo));
			}

			return {
				...state,
				payment: paymentInfo,
			};
		}

		case UPDATE_SELECTION: {
			setCookie('selection', action.data.id, 365);
			let totalQuantity = 0;

			return {
				...state,
				selectionError: false,
				selectionErrorStock: false,
				addToBagError: false,
				selection: { // Replace all data every time.
					token: action.data.id,
					currency: action.data.currencyCode,
					items: action.data.lineItems.edges.map((item) => {
						const variantId = atob(item.node.variant.product.id);
						const consolidatedVariant = item.node.variant.selectedOptions.reduce((acc, option) => (option.name.toLowerCase() === 'size' ? acc : [...acc, urlifyAllowAccents(option.value)]), []).join('_');
						totalQuantity += item.node.quantity;

						return ({
							id: item.node.variant.id,
							line: item.node.id,
							quantity: item.node.quantity,
							cacheId: `${variantId.slice(variantId.lastIndexOf('/') + 1)}${consolidatedVariant ? `_${consolidatedVariant}` : ''}`,
							selectedOptions: { ...item.node.variant.selectedOptions.reduce((acc, option) => ({ ...acc, [camelCase(option.name)]: option.value }), {}) },
							price: { [action.data.currencyCode]: { price: item.node.variant.priceV2.amount, compareAtPrice: item?.node?.variant?.compareAtPriceV2?.amount } },
							name: item.node.variant.product.title,
							sku: item.node.variant.sku,
						});
					}),
					totals: {
						lineItemsSubtotal: action.data.lineItemsSubtotalPrice.amount,
						totalTax: action.data.totalTaxV2.amount,
						subtotal: action.data.subtotalPriceV2.amount,
						total: action.data.totalPriceV2.amount,
						taxExempt: action.data.taxExempt,
						taxesIncluded: action.data.taxesIncluded,
						totalQuantity,
						totalItems: action.data.lineItems.edges.length,
					},
					webUrl: action.data.webUrl,
				},
			};
		}

		case UPDATE_ADDITIONAL_INFORMATION: {
			// Send empty note to delete from additional information.
			const exists = state.payment.additionalInformation.reduce((acc, item, i) => (item.item === action.note.item ? i : acc), -1);
			let additionalInformation = [];

			if (exists === -1 && action.note && action.note.message !== '') {
				// Add
				additionalInformation = [...state.payment.additionalInformation, { ...action.note }];
			} else if (!action.note || action.note.message === '') {
				// Remove
				additionalInformation = [...state.payment.additionalInformation.slice(0, exists), ...state.payment.additionalInformation.slice(exists + 1)];
			} else {
				// Update
				additionalInformation = [...state.payment.additionalInformation.slice(0, exists), { ...action.note }, ...state.payment.additionalInformation.slice(exists + 1)];
			}

			const paymentInfo = {
				...state.payment,
				additionalInformation,
			};

			if (typeof localStorage !== 'undefined') localStorage.setItem('paymentInfo', JSON.stringify(paymentInfo));

			return {
				...state,
				payment: paymentInfo,
			};
		}

		case 'INIT_PAYMENT_INFO': {
			return {
				...state,
				payment: {
					...state.payment,
					...action.paymentInfo,
				},
			};
		}

		case UPDATE_ERRORS: {
			if (action.group) {
				const field = action.group === 'shippingAddress' ? state.selection.paymentFields.address[action.id] : state.selection.paymentFields[action.group][action.id];
				if (action.id === 'state') field.required = true;

				return {
					...state,
					selection: {
						...state.selection,
						paymentFields: {
							...state.selection.paymentFields,
							[action.group]: {
								...state.selection.paymentFields[action.group],
								[action.id]: {
									...state.selection.paymentFields[action.group][action.id],
									error: field.visible && field.required ? action.error : false,
								},
							},
						},
					},
				};
			}
			if (state.selection.paymentFields[action.id]) {
				const singleField = state.selection.paymentFields[action.id];
				return {
					...state,
					selection: {
						...state.selection,
						paymentFields: {
							...state.selection.paymentFields,
							[action.id]: {
								...state.selection.paymentFields[action.id],
								error: singleField.visible && singleField.required ? action.error : false,
							},
						},
					},
				};
			}
			return { ...state };
		}

		case BOOLEAN_CHANGE: {
			if (action.id === 'useShipping') {
				return {
					...state,
					selectionError: false,
					selectionErrorStock: false,
					useShippingAddress: !state.useShippingAddress,
					payment: {
						...state.payment,
						shippingAddress: {
							...state.payment.shippingAddress,
							email: state.payment.shippingAddress.email || state.payment.address.email,
							firstName: state.payment.shippingAddress.firstName || state.payment.address.firstName,
							lastName: state.payment.shippingAddress.lastName || state.payment.address.lastName,
							company: state.payment.shippingAddress.company || state.payment.address.company,
							phone: state.payment.shippingAddress.phone || state.payment.address.phone,
							address1: state.payment.shippingAddress.address1 || state.payment.address.address1,
							address2: state.payment.shippingAddress.address2 || state.payment.address.address2,
							zip: state.payment.shippingAddress.zip || state.payment.address.zip,
							city: state.payment.shippingAddress.city || state.payment.address.city,
							province: state.payment.shippingAddress.province || state.payment.address.province,
							country: state.payment.shippingAddress.country || state.payment.address.country,
						},
					},
				};
			}
			if (action.id === 'subscribeToNewsletter') {
				return {
					...state,
					selectionError: false,
					selectionErrorStock: false,
					payment: {
						...state.payment,
						address: {
							...state.payment.address,
							newsletter: !state.payment.address.newsletter,
						},
					},
				};
			}

			const paymentInfo = {
				...state.payment,
				[action.id]: !state.payment[action.id],
			};

			localStorage.setItem('paymentInfo', JSON.stringify(paymentInfo));

			return {
				...state,
				selectionError: false,
				selectionErrorStock: false,
				payment: paymentInfo,
			};
		}

		case VOUCHER_INPUT: {
			return {
				...state,
				selectionError: false,
				selectionErrorStock: false,
				vouchers: {
					...state.vouchers,
					currentValue: action.value,
					error: false,
				},
			};
		}

		case VOUCHER_ERROR: {
			return {
				...state,
				vouchers: {
					...state.vouchers,
					error: !state.vouchers.error,
				},
			};
		}

		case DESTROY_SELECTION: {
			localStorage.removeItem('selectionCache');
			deleteCookie('selection');
			deleteCookie('receiptShown');

			return {
				...state,
				items: {},
				selectionCache: [],
				useShippingAddress: false,
				vouchers: {
					currentValue: '',
					error: false,
				},
				payment: {
					...state.payment,
					termsAndConditions: false,
				},
				paymentReturn: {},
				addToBagError: false,
				selectionError: false,
				selectionErrorStock: false,
				selection: {
					token: '',
				},
				destroySelection: false,
			};
		}

		default:
			return state;
	}
};

export { initialState as checkoutState };
export default checkout;
